import { useMemo, useState } from 'react';
import { appendCapiumTag, capiumTagGroupsFor } from '@/core/models/capium-tags';
import {
  amountPayload,
  applyRowTotal,
  buildAmountRows,
  fillPreviousFromPrior,
  narrativeEditorConfig,
  priorSingleton,
  type EditableAmountRow,
  type NoteDialogData,
  type NoteRow,
  type NoteType,
} from '@/core/models/notes.models';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import type { DialogComponentProps } from '@/kit/dialog';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { AmountRowsTable } from './AmountRowsTable';
import { NoteDialogShell, useNoteSave } from './NoteDialogParts';

interface AmountRowsDialogProps extends DialogComponentProps<NoteDialogData, NoteRow> {
  noteType: NoteType;
  defaultTitle: string;
  /** Staff costs: the last row is the total of the ones above it. */
  hasTotal?: boolean;
  nameHeaderClassName?: string;
  headerClassName?: string;
}

/** The current-year / previous-year amount grid dialog (directors' emoluments, staff costs, average employees). */
function AmountRowsDialog({
  data,
  dialogRef,
  noteType,
  defaultTitle,
  hasTotal = false,
  nameHeaderClassName,
  headerClassName,
}: AmountRowsDialogProps) {
  const recalc = (list: EditableAmountRow[]): EditableAmountRow[] => {
    const copy = list.map((row) => ({ ...row }));
    if (hasTotal) {
      applyRowTotal(copy);
    }
    return copy;
  };

  const [rows, setRows] = useState<EditableAmountRow[]>(() => {
    const initial = buildAmountRows(noteType, data?.note?.content?.rows);
    if (data) {
      fillPreviousFromPrior(initial, priorSingleton(data));
    }
    return recalc(initial);
  });
  const [contents, setContents] = useState<string>(() => data?.note?.content?.contents ?? '');
  const editorConfig = useMemo(() => narrativeEditorConfig(), []);
  const { isSaving, save } = useNoteSave(data, dialogRef);

  const isTotal = (index: number) => hasTotal && index === rows.length - 1;

  return (
    <NoteDialogShell
      title={data?.title || defaultTitle}
      headerClassName={headerClassName}
      isSaving={isSaving}
      onSave={() =>
        save(() => {
          const total = recalc(rows);
          setRows(total);
          return { rows: amountPayload(total), contents };
        })
      }
    >
      <div className="d-flex gap-15 pt-5">
        <div>
          <div className="form-field w-510">
            <p className="fw-bold">Note Contents</p>
            {/* APT247: the formatting toolbar lines up under the box (NotesDialogs.scss .note-editor). */}
            <div className="note-editor">
              <RichTextEditor value={contents} onChange={setContents} config={editorConfig} />
            </div>
          </div>
          <AmountRowsTable
            rows={rows}
            nameHeaderClassName={nameHeaderClassName}
            isTotal={isTotal}
            onCellChange={(index, column, value) =>
              setRows((current) => current.map((row, i) => (i === index ? { ...row, [column]: value } : row)))
            }
            onCellBlur={hasTotal ? () => setRows((current) => recalc(current)) : undefined}
          />
        </div>
        <NoteTagsTab
          groups={capiumTagGroupsFor(data?.clientType ?? (data?.isLlp ? 3 : null))}
          onInsert={(tag) => setContents((current) => appendCapiumTag(current, tag))}
        />
      </div>
    </NoteDialogShell>
  );
}

const AMOUNT_HEADER = 'sidenav-header border-none mb-0';

/** Directors' Emoluments (ap5-a5: the heading follows the note the dialog was opened for). */
export function DirectorEmolumentDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <AmountRowsDialog
      {...props}
      noteType="directorEmoluments"
      defaultTitle="Directors' Emoluments"
      nameHeaderClassName="w-150"
      headerClassName={AMOUNT_HEADER}
    />
  );
}

export function StaffCostsDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <AmountRowsDialog
      {...props}
      noteType="staffCosts"
      defaultTitle="Staff Costs"
      hasTotal
      headerClassName={AMOUNT_HEADER}
    />
  );
}

export function NumberofEmployeesDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <AmountRowsDialog
      {...props}
      noteType="averageEmployees"
      defaultTitle="Average number of employees"
      nameHeaderClassName="w-150"
      headerClassName={AMOUNT_HEADER}
    />
  );
}
